import { STORAGE_NAMESPACE } from "../config";
import {
  isLanguage,
  LANGUAGE_LABELS,
  LANGUAGES,
  type Language,
  language,
  languageOf,
  onLanguageChange,
  phrases,
  setLanguage,
} from "../i18n";
import type { PlatformAdapter } from "../platform/adapter";
import { make, makeButton } from "./dom";

/**
 * The page's language (L10N1): chosen at boot, written into the front page's marked elements,
 * and switched by a row of pennants on a gold rod (the front page's corner, and the info window
 * in the game). The choice stays in this browser only; nothing records it. No Pixi here.
 */

/** The visitor's pick on this browser; absent until a pennant is pressed. */
export const LANGUAGE_KEY = `${STORAGE_NAMESPACE}:language`;
/** `?lang=ru`: this page in a language, before the kept pick (a shared link, the tests). */
const LANGUAGE_PARAM = "lang";

/** What the address asks for, the kept pick, else the browser's preferred languages. */
export function chosenLanguage(options: {
  readonly search: string;
  readonly kept: string | null;
  readonly preferred: readonly string[];
}): Language {
  const asked = new URLSearchParams(options.search).get(LANGUAGE_PARAM);
  if (isLanguage(asked)) {
    return asked;
  }
  return isLanguage(options.kept) ? options.kept : languageOf(options.preferred);
}

/** The address with its `lang` set to `next`, if it named one: a reload keeps the pick. */
export function addressInLanguage(search: string, next: Language): string {
  const params = new URLSearchParams(search);
  if (!params.has(LANGUAGE_PARAM)) {
    return search;
  }
  params.set(LANGUAGE_PARAM, next);
  return `?${params.toString()}`;
}

/** Speaks the chosen language from now on, and says so to the document. */
export function startLanguage(store: PlatformAdapter["data"]): void {
  let kept: string | null = null;
  try {
    kept = store.get(LANGUAGE_KEY);
  } catch {
    // A store that cannot be read keeps no pick.
  }
  setLanguage(
    chosenLanguage({ search: location.search, kept, preferred: navigator.languages ?? [] }),
  );
  document.documentElement.lang = language();
  onLanguageChange((next) => {
    document.documentElement.lang = next;
  });
}

type PagePhrase = keyof ReturnType<typeof phrases>["front"];

function pagePhrase(key: string | undefined): string | undefined {
  const value = key === undefined ? undefined : phrases().front[key as PagePhrase];
  return typeof value === "string" ? value : undefined;
}

/** L10N3: the privacy note in `language` (`public/privacy.<code>.html`); English is the note itself. */
export function privacyAddress(spoken: Language): string {
  return spoken === "en" ? "./privacy.html" : `./privacy.${spoken}.html`;
}

/**
 * Writes the language into `root`'s marked elements: `data-phrase` its text, `data-phrase-title`
 * its tooltip, `data-phrase-label` its accessible name; a `data-privacy` link points at the note in
 * the language (L10N3). A button busy loading keeps its word.
 */
export function writePhrases(root: ParentNode): void {
  for (const element of root.querySelectorAll<HTMLElement>("[data-phrase]")) {
    const text = pagePhrase(element.dataset.phrase);
    if (text !== undefined && element.getAttribute("aria-busy") !== "true") {
      element.textContent = text;
    }
  }
  for (const element of root.querySelectorAll<HTMLElement>("[data-phrase-title]")) {
    const text = pagePhrase(element.dataset.phraseTitle);
    if (text !== undefined) {
      element.title = text;
    }
  }
  for (const link of root.querySelectorAll<HTMLAnchorElement>("a[data-privacy]")) {
    link.href = privacyAddress(language());
    link.hreflang = language();
  }
  for (const element of root.querySelectorAll<HTMLElement>("[data-phrase-label]")) {
    const text = pagePhrase(element.dataset.phraseLabel);
    if (text !== undefined) {
      element.setAttribute("aria-label", text);
    }
  }
}

/**
 * The pennants: one a language, its two letters on the cloth, its own name on hover; the spoken
 * one hangs lower in the kingdom's orange. A press switches the page at once and keeps the pick.
 */
export function mountLanguagePicker(options: {
  readonly id: string;
  readonly store: PlatformAdapter["data"];
}): HTMLElement {
  const row = make("div", { id: options.id, className: "pennants" });
  row.setAttribute("role", "radiogroup");
  const pennants = LANGUAGES.map((each) => {
    const pennant = makeButton({ className: "pennant" });
    pennant.lang = each;
    pennant.dataset.language = each;
    pennant.title = LANGUAGE_LABELS[each].name;
    pennant.setAttribute("role", "radio");
    pennant.setAttribute("aria-label", LANGUAGE_LABELS[each].name);
    const edge = make("span", { className: "edge" });
    edge.append(make("span", { className: "cloth", text: LANGUAGE_LABELS[each].mark }));
    pennant.append(edge);
    pennant.addEventListener("click", () => {
      try {
        options.store.set(LANGUAGE_KEY, each);
      } catch {
        // A refused write leaves the pick to this page.
      }
      history.replaceState(
        history.state,
        "",
        `${location.pathname}${addressInLanguage(location.search, each)}${location.hash}`,
      );
      setLanguage(each);
    });
    return pennant;
  });
  row.append(...pennants);
  const show = (): void => {
    row.setAttribute("aria-label", phrases().front.language);
    for (const pennant of pennants) {
      const spoken = pennant.dataset.language === language();
      pennant.setAttribute("aria-checked", String(spoken));
      pennant.tabIndex = spoken ? 0 : -1;
    }
  };
  show();
  onLanguageChange(show);
  // A radio group moves its choice with the arrow keys.
  row.addEventListener("keydown", (event) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (step === undefined) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const at = LANGUAGES.indexOf(language());
    const next = pennants[(at + step + pennants.length) % pennants.length];
    next?.click();
    next?.focus();
  });
  return row;
}
